import { useQueryClient } from '@tanstack/react-query';
import { DashboardBriefDto, DashboardDto } from '@thingslog/repositories';
import { QueryKeys } from '@thingslog/queries';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dashboardQueryClient } from '../../../clients/ReactQueryClients/ReactQueryClients';

const DASHBOARD_SELECTOR_QUERY_PARAM = 'dashboardSelector';
const DASHBOARD_SELECTOR_OPEN_VALUE = 'open';

interface UseDashboardSelectionResult {
  isDashboardSelectionSidebarOpen: boolean;
  handleDashboardSelectionToggle: () => void;
  handleSelectDashboard: (dashboardId: string) => void;
  handleViewDashboard: (dashboardId: string) => void;
  handleSelectDefaultDashboard: (dashboards: DashboardBriefDto[]) => void;
  handleNavigateToDashboards: () => void;
  handleSetDefaultDashboard: (dashboardId: string) => void;
  handleToggleFavoriteDashboard: (dashboardId: string, isFavorite: boolean) => void;
}

export const useDashboardSelection = (
  companyId?: string | number | null
): UseDashboardSelectionResult => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { useSetDefaultDashboard, useSetFavoriteDashboard } = dashboardQueryClient;
  const queryCompanyId = companyId ?? undefined;

  const dashboardSelectorSearchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const isDashboardSelectionSidebarOpen =
    dashboardSelectorSearchParams.get(DASHBOARD_SELECTOR_QUERY_PARAM) ===
    DASHBOARD_SELECTOR_OPEN_VALUE;

  const handleDashboardSelectionToggle = useCallback((): void => {
    const nextSearchParams = new URLSearchParams(location.search);

    if (isDashboardSelectionSidebarOpen) {
      nextSearchParams.delete(DASHBOARD_SELECTOR_QUERY_PARAM);
    } else {
      nextSearchParams.set(DASHBOARD_SELECTOR_QUERY_PARAM, DASHBOARD_SELECTOR_OPEN_VALUE);
    }

    const nextSearch = nextSearchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [isDashboardSelectionSidebarOpen, location.pathname, location.search, navigate]);

  const handleSelectDashboard = useCallback(
    (dashboardId: string): void => {
      const nextSearchParams = new URLSearchParams(location.search);
      nextSearchParams.delete(DASHBOARD_SELECTOR_QUERY_PARAM);
      const nextSearch = nextSearchParams.toString();

      navigate({
        pathname: `/app/dashboards/${dashboardId}`,
        search: nextSearch ? `?${nextSearch}` : '',
      });
    },
    [location.search, navigate]
  );

  const handleViewDashboard = useCallback(
    (dashboardId: string): void => {
      navigate(`/app/dashboards/${dashboardId}`);
    },
    [navigate]
  );

  const handleNavigateToDashboards = useCallback((): void => {
    navigate('/app/dashboards');
  }, [navigate]);

  const invalidateDashboardPreferenceQueries = useCallback(
    (dashboardId: string): void => {
      queryClient.invalidateQueries([QueryKeys.GetDashboards]);
      queryClient.invalidateQueries([QueryKeys.GetDashboard, dashboardId, queryCompanyId]);
    },
    [queryClient, queryCompanyId]
  );

  const updateCachedDashboardList = useCallback(
    (updateDashboard: (dashboard: DashboardBriefDto) => DashboardBriefDto): void => {
      queryClient.setQueryData<DashboardBriefDto[]>(
        [QueryKeys.GetDashboards, queryCompanyId],
        (dashboards: DashboardBriefDto[] | undefined): DashboardBriefDto[] | undefined =>
          dashboards?.map(updateDashboard)
      );
    },
    [queryClient, queryCompanyId]
  );

  const updateCachedDashboardDetails = useCallback(
    (dashboardId: string, updateDashboard: (dashboard: DashboardDto) => DashboardDto): void => {
      queryClient.setQueryData<DashboardDto>(
        [QueryKeys.GetDashboard, dashboardId, queryCompanyId],
        (dashboard: DashboardDto | undefined): DashboardDto | undefined =>
          dashboard ? updateDashboard(dashboard) : dashboard
      );
    },
    [queryClient, queryCompanyId]
  );

  const setDefaultDashboardMutation = useSetDefaultDashboard({
    onSuccess: (dashboard: DashboardDto): void => {
      updateCachedDashboardList(
        (cachedDashboard: DashboardBriefDto): DashboardBriefDto =>
          cachedDashboard.id === dashboard.id
            ? {
                ...cachedDashboard,
                isDefault: true,
                isFavorite: dashboard.isFavorite,
              }
            : {
                ...cachedDashboard,
                isDefault: false,
              }
      );
      updateCachedDashboardDetails(
        dashboard.id,
        (cachedDashboard: DashboardDto): DashboardDto => ({
          ...cachedDashboard,
          isDefault: true,
          isFavorite: dashboard.isFavorite,
        })
      );
    },
    onSettled: (
      _dashboard: DashboardDto | undefined,
      _error: unknown,
      variables: { id: string; companyId?: string | number | null } | undefined
    ): void => {
      if (variables) {
        invalidateDashboardPreferenceQueries(variables.id);
      }
    },
  });

  const setFavoriteDashboardMutation = useSetFavoriteDashboard({
    onSuccess: (dashboard: DashboardDto): void => {
      updateCachedDashboardList(
        (cachedDashboard: DashboardBriefDto): DashboardBriefDto =>
          cachedDashboard.id === dashboard.id
            ? {
                ...cachedDashboard,
                isFavorite: dashboard.isFavorite,
              }
            : cachedDashboard
      );
      updateCachedDashboardDetails(
        dashboard.id,
        (cachedDashboard: DashboardDto): DashboardDto => ({
          ...cachedDashboard,
          isFavorite: dashboard.isFavorite,
          isDefault: dashboard.isDefault,
        })
      );
    },
    onSettled: (
      _dashboard: DashboardDto | undefined,
      _error: unknown,
      variables: { id: string; isFavorite: boolean; companyId?: string | number | null } | undefined
    ): void => {
      if (variables) {
        invalidateDashboardPreferenceQueries(variables.id);
      }
    },
  });

  const handleSelectDefaultDashboard = useCallback(
    (dashboards: DashboardBriefDto[]): void => {
      if (dashboards.length === 0) {
        navigate('/app/dashboards');
        return;
      }

      const defaultDashboard = dashboards.find((dashboard: DashboardBriefDto): boolean =>
        Boolean(dashboard.isDefault)
      );

      if (defaultDashboard) {
        navigate(`/app/dashboards/${defaultDashboard.id}`);
        return;
      }

      navigate('/app/dashboards');
    },
    [navigate]
  );

  const handleSetDefaultDashboard = useCallback(
    (dashboardId: string): void => {
      updateCachedDashboardList(
        (dashboard: DashboardBriefDto): DashboardBriefDto => ({
          ...dashboard,
          isDefault: dashboard.id === dashboardId,
        })
      );
      updateCachedDashboardDetails(
        dashboardId,
        (dashboard: DashboardDto): DashboardDto => ({
          ...dashboard,
          isDefault: true,
        })
      );

      setDefaultDashboardMutation.mutate({
        id: dashboardId,
        companyId: queryCompanyId,
      });
    },
    [
      queryCompanyId,
      setDefaultDashboardMutation,
      updateCachedDashboardDetails,
      updateCachedDashboardList,
    ]
  );

  const handleToggleFavoriteDashboard = useCallback(
    (dashboardId: string, isFavorite: boolean): void => {
      updateCachedDashboardList(
        (dashboard: DashboardBriefDto): DashboardBriefDto =>
          dashboard.id === dashboardId
            ? {
                ...dashboard,
                isFavorite,
              }
            : dashboard
      );
      updateCachedDashboardDetails(
        dashboardId,
        (dashboard: DashboardDto): DashboardDto => ({
          ...dashboard,
          isFavorite,
        })
      );

      setFavoriteDashboardMutation.mutate({
        id: dashboardId,
        isFavorite,
        companyId: queryCompanyId,
      });
    },
    [
      queryCompanyId,
      setFavoriteDashboardMutation,
      updateCachedDashboardDetails,
      updateCachedDashboardList,
    ]
  );

  return {
    isDashboardSelectionSidebarOpen,
    handleDashboardSelectionToggle,
    handleSelectDashboard,
    handleViewDashboard,
    handleSelectDefaultDashboard,
    handleNavigateToDashboards,
    handleSetDefaultDashboard,
    handleToggleFavoriteDashboard,
  };
};
